/**
 * src/pages/CriticalThinking/data/belief.data.ts
 * Data for Section 5: Belief Revision Exercise & Section 6: What Would Change My Mind.
 * Teaches: calibrating confidence, updating on evidence stages, avoiding dogmatic cling,
 * and recognizing that revising a belief is an intellectual strength.
 */

export interface EvidenceStage {
  stageNumber: number;
  stageTitle: string;
  evidenceRevealed: string;
  sourceReliabilityNote: string;
  recommendedConfidenceRange: [number, number];
  expertRationale: string;
}

export interface BeliefRevisionScenario {
  id: string;
  title: string;
  coreHypothesis: string;
  initialContext: string;
  stages: EvidenceStage[];
  synthesisTakeaway: string;
}

export const BELIEF_SCENARIO: BeliefRevisionScenario = {
  id: "telecom-interference",
  title: "The Mysterious Observatory Signal Loss",
  coreHypothesis: "Hypothesis: The high-latitude solar observatory's midnight data dropouts are caused by the new 5G cellular transmission mast erected 4 km to the south.",
  initialContext: "You are the chief systems engineer at the Crestview Solar Observatory. Starting three weeks ago, precision microwave sensor data began dropping out intermittently between 11:30 PM and 1:30 AM on weekdays. A local wireless provider turned on a high-power commercial telecommunications tower 4 km south just four days before the dropouts started. The local town council is already drafting a formal complaint.",
  stages: [
    {
      stageNumber: 1,
      stageTitle: "Stage 1: The Initial Temporal Coincidence",
      evidenceRevealed: "You review the system logs. The tower activation date was October 12th. The first recorded midnight data drop was October 16th. The tower broadcasts on a 3.6 GHz carrier frequency. Your telescope's primary receiver operates at 1.4 GHz (hydrogen line) but uses a wideband intermediate amplifier with harmonic sensitivity.",
      sourceReliabilityNote: "Direct internal observatory telemetry logs. High reliability for timing, but temporal correlation alone does not prove radio frequency interference (RFI).",
      recommendedConfidenceRange: [45, 65],
      expertRationale: "Given the close temporal timing and wideband amplifier vulnerability, it is entirely reasonable to assign moderate suspicion (~50-60%). Jumping to 95%+ certainty here would be premature, as many facility and atmospheric variables fluctuate."
    },
    {
      stageNumber: 2,
      stageTitle: "Stage 2: Frequency Sweep & Directional Shielding Test",
      evidenceRevealed: "Your team positions a directional RF spectrum analyzer at the tower. When the tower transmits high packet bursts, the 3.6 GHz band is active, but your spectrum analyzer detects zero 1.4 GHz harmonic bleed. Furthermore, installing temporary copper mesh RF shielding on the south-facing antenna wall produces zero reduction in the midnight dropouts.",
      sourceReliabilityNote: "High empirical reliability: physical measurement with a calibrated spectrum analyzer and controlled shielding experiment.",
      recommendedConfidenceRange: [15, 30],
      expertRationale: "This is substantial disconfirming evidence. If the tower were directly jamming the sensor, the directional shielding or spectrum analyzer would have picked up the signature. A rational investigator drastically lowers their confidence, moving from suspect (~55%) down to low probability (~20%)."
    },
    {
      stageNumber: 3,
      stageTitle: "Stage 3: The Scheduled Tower Maintenance Outage",
      evidenceRevealed: "The telecom company cooperates and shuts down the tower completely for 48 hours over Tuesday and Wednesday night for scheduled antenna maintenance. During both nights of total tower shutdown, your observatory's midnight data dropouts continue at the exact same intensity from 11:45 PM to 1:15 AM.",
      sourceReliabilityNote: "Gold-standard intervention test: the putative cause was completely removed from the environment while the effect persisted identically.",
      recommendedConfidenceRange: [0, 8],
      expertRationale: "The putative cause is absent, yet the effect continues unabated. In formal logic and experimental science, this decisively severs the proposed direct causal link. A skilled critical thinker lowers their confidence to near zero (~2-5%). Clinging to the tower theory now would represent pure confirmation bias."
    },
    {
      stageNumber: 4,
      stageTitle: "Stage 4: The Discovery of the Real Mechanism",
      evidenceRevealed: "An electrical technician inspects the observatory basement and discovers that three weeks ago, facility management installed an automated cryogenic chiller cycle set on a digital timer to cool the helium compressors every weekday at 11:30 PM. The chiller's secondary grounding wire is loose, injecting massive electromagnetic pulse spikes directly into the observatory's building power bus.",
      sourceReliabilityNote: "Direct physical discovery with an identified causal mechanism that completely explains the timing, internal origin, and dropouts.",
      recommendedConfidenceRange: [0, 2],
      expertRationale: "The mystery is completely resolved by a verified, reproducible internal mechanism. Confidence in the telecom tower hypothesis should now be 0%. Revising beliefs when decisive evidence emerges is the pinnacle of scientific thinking."
    }
  ],
  synthesisTakeaway: "Belief revision is not a sign of weakness, flip-flopping, or failure—it is the core mechanic of rationality. Notice how initial suspicion was completely justified by timing, but clinging to the tower hypothesis after the shutdown test would have been dogma."
};

export interface MindChangePracticeItem {
  id: string;
  claim: string;
  potentialObservations: {
    id: string;
    text: string;
    isGenuineLowerer: boolean;
    explanation: string;
  }[];
}

export const MIND_CHANGE_ITEMS: MindChangePracticeItem[] = [
  {
    id: "organic-yield",
    claim: "Claim: Switching our region entirely to organic agricultural methods will easily match conventional crop yields without needing more land.",
    potentialObservations: [
      {
        id: "o1",
        text: "A 5-year multi-site university agronomical trial showing organic cereal crop yields averaged 22% lower per hectare across varying rainfall years.",
        isGenuineLowerer: true,
        explanation: "Genuinely lowers confidence: direct empirical yield-per-hectare data directly tests and contradicts the equal-yield assertion."
      },
      {
        id: "o2",
        text: "An organic food brand sponsoring a television cooking show.",
        isGenuineLowerer: false,
        explanation: "Irrelevant marketing activity: sponsorship has no bearing on agricultural yield physics."
      },
      {
        id: "o3",
        text: "A conventional chemical fertilizer executive making an angry speech on cable news.",
        isGenuineLowerer: false,
        explanation: "Tone and partisan conflict: an opponent being angry does not measure crop bushel harvests."
      }
    ]
  },
  {
    id: "remote-productivity",
    claim: "Claim: Mandating five days a week in the physical office invariably increases team software delivery speed compared to flexible remote work.",
    potentialObservations: [
      {
        id: "o1",
        text: "A rigorous company-wide audit tracking pull-request cycle times, code bug rates, and feature velocity showing zero improvement after the in-office mandate, alongside a 35% surge in senior engineer turnover.",
        isGenuineLowerer: true,
        explanation: "Genuinely lowers confidence: objectively measures the exact variables (delivery speed, quality, retention) claimed to improve."
      },
      {
        id: "o2",
        text: "The CEO expressing a personal belief that 'seeing people in chairs feels more productive'.",
        isGenuineLowerer: false,
        explanation: "Subjective executive impression: feelings do not substitute for objective development metrics."
      },
      {
        id: "o3",
        text: "Office coffee beans costing 15% more this fiscal quarter.",
        isGenuineLowerer: false,
        explanation: "Incidental facility expense that has no bearing on software engineering velocity."
      }
    ]
  },
  {
    id: "speed-cameras",
    claim: "Claim: Installing automated radar speed cameras at high-crash intersections has zero impact on reducing fatal vehicle collisions.",
    potentialObservations: [
      {
        id: "o1",
        text: "Ten years of state Department of Transportation trauma data controlling for weather and traffic volume showing a sustained 42% drop in severe T-bone collisions at the camera intersections.",
        isGenuineLowerer: true,
        explanation: "Genuinely lowers confidence: direct epidemiological accident reduction data refutes the 'zero impact' hypothesis."
      },
      {
        id: "o2",
        text: "An online petition with 3,000 angry drivers calling the speed cameras a 'cash grab'.",
        isGenuineLowerer: false,
        explanation: "Popular dislike of traffic enforcement does not measure collision biomechanics or hospital injury rates."
      },
      {
        id: "o3",
        text: "A local newspaper running a photo of a camera painted yellow.",
        isGenuineLowerer: false,
        explanation: "Cosmetic paint color is irrelevant to radar enforcement efficacy."
      }
    ]
  }
];

export interface FinalChallengeQuestion {
  id: string;
  scenario: string;
  question: string;
  options: {
    id: string;
    text: string;
    isCorrect: boolean;
    feedback: string;
  }[];
}

export const FINAL_CHALLENGE: FinalChallengeQuestion[] = [
  {
    id: "q1",
    scenario: "A pharmaceutical research lab reports that a new synthetic molecule destroyed cancer cells in a petri dish with 99% efficacy. A viral health blog writes: 'Cancer cure finalized! Daily pill will eradicate human tumors within months.'",
    question: "What is the primary critical thinking flaw in the blog's conclusion?",
    options: [
      {
        id: "a",
        text: "The researchers did not use standard glassware in the laboratory.",
        isCorrect: false,
        feedback: "Glassware type is an irrelevant technical detail."
      },
      {
        id: "b",
        text: "Failing to distinguish between in-vitro cell culture assays and complex in-vivo human pharmacokinetics, safety, toxicity, and clinical trials.",
        isCorrect: true,
        feedback: "Exact match: bleach or soapy water also kills cancer cells in a petri dish, but cannot be swallowed safely. Jumping from in-vitro dish to human cure is the classic translational error."
      },
      {
        id: "c",
        text: "Cancer does not actually exist in mammalian biology.",
        isCorrect: false,
        feedback: "Factually false: oncology is an established biological reality."
      }
    ]
  },
  {
    id: "q2",
    scenario: "An investigator looking into election fraud notes that in a rural county, 14 registered voters had birthdays listed as January 1st, 1900. They conclude: 'The entire election was systematically rigged by zombie algorithms.'",
    question: "What alternative explanation should a disciplined critical thinker evaluate first?",
    options: [
      {
        id: "a",
        text: "Default placeholder database dates: older database software frequently defaulted blank or corrupted date fields to '1900-01-01'.",
        isCorrect: true,
        feedback: "Accurate: understanding technical edge cases and database defaults is parsimonious compared to jumping straight to vast algorithmic conspiracies."
      },
      {
        id: "b",
        text: "Those 14 voters drank the mountain immortality root elixir.",
        isCorrect: false,
        feedback: "Conflates folklore with technical investigation."
      },
      {
        id: "c",
        text: "The county does not use calendar months.",
        isCorrect: false,
        feedback: "Absurd non-starter."
      }
    ]
  },
  {
    id: "q3",
    scenario: "A colleague reviews a proposal to update your department's security protocol. They announce: 'There is zero proof that our current server will be hacked this Friday. Therefore, spending any budget on security updates is completely irrational.'",
    question: "What epistemic distinction is the colleague confusing?",
    options: [
      {
        id: "a",
        text: "Confusing 'not currently established as an immediate certainty' with 'safely proven to be zero risk', committing an appeal to ignorance.",
        isCorrect: true,
        feedback: "Exactly: absence of immediate evidence is not evidence of absence of risk. Risk management deals with probability distributions and vulnerabilities, not waiting for a catastrophe to occur first."
      },
      {
        id: "b",
        text: "The colleague is using an obsolete mouse pad.",
        isCorrect: false,
        feedback: "Irrelevant personal detail."
      },
      {
        id: "c",
        text: "Security software is free of charge worldwide.",
        isCorrect: false,
        feedback: "Commercial enterprise software carries real costs."
      }
    ]
  }
];
